import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { LoanRestructure } from '@/types';

// Custom endpoint — keep manual
export function useRestructures(loanId: string) {
  return useQuery({
    queryKey: ['restructures', loanId],
    queryFn: () => api.get(`/loans/${loanId}/restructures`).then(r => r.data as LoanRestructure[]),
    enabled: !!loanId,
  });
}

// Custom endpoint — keep manual
export function useCreateRestructure(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<LoanRestructure>) =>
      api.post(`/loans/${loanId}/restructures`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['restructures', loanId] }),
  });
}

// Custom action endpoint /submit — keep manual
export function useSubmitRestructure(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post(`/loans/${loanId}/restructures/${id}/submit`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['restructures', loanId] }),
  });
}

// Custom action endpoint /approve — keep manual
export function useApproveRestructure(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post(`/loans/${loanId}/restructures/${id}/approve`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['restructures', loanId] }),
  });
}

// Custom action endpoint /reject with payload — keep manual
export function useRejectRestructure(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/loans/${loanId}/restructures/${id}/reject`, { reason }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['restructures', loanId] }),
  });
}

// Custom action endpoint /apply with extra invalidation — keep manual
export function useApplyRestructure(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post(`/loans/${loanId}/restructures/${id}/apply`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['restructures', loanId] });
      qc.invalidateQueries({ queryKey: ['loans', loanId] });
    },
  });
}
